import * as Sentry from '@sentry/react';

interface FunctionMetric {
  calls: number;
  errors: number;
  latencies: number[];
  avgLatency: number;
  minLatency: number;
  maxLatency: number;
  p95Latency: number;
}

interface Metrics {
  calls: Record<string, FunctionMetric>;
  totalCalls: number;
  totalErrors: number;
  startTime: number;
}

const CloudFunctionsMetrics: Metrics = {
  calls: {},
  totalCalls: 0,
  totalErrors: 0,
  startTime: Date.now(),
};

export const trackCloudFunctionCall = async <T>(
  functionName: string,
  executeFunction: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();
  const callId = `${functionName}-${Date.now()}-${Math.random()}`;

  try {
    if (!CloudFunctionsMetrics.calls[functionName]) {
      CloudFunctionsMetrics.calls[functionName] = {
        calls: 0,
        errors: 0,
        latencies: [],
        avgLatency: 0,
        minLatency: Infinity,
        maxLatency: 0,
        p95Latency: 0,
      };
    }

    const result = await executeFunction();

    const duration = performance.now() - startTime;
    const metric = CloudFunctionsMetrics.calls[functionName];

    metric.calls += 1;
    metric.latencies.push(duration);
    metric.avgLatency = metric.latencies.reduce((a, b) => a + b, 0) / metric.latencies.length;
    metric.minLatency = Math.min(metric.minLatency, duration);
    metric.maxLatency = Math.max(metric.maxLatency, duration);

    if (metric.latencies.length > 50) {
      metric.latencies.shift();
    }

    if (metric.latencies.length >= 10) {
      const sorted = [...metric.latencies].sort((a, b) => a - b);
      const p95Index = Math.ceil(sorted.length * 0.95) - 1;
      metric.p95Latency = sorted[Math.max(0, p95Index)];
    }

    CloudFunctionsMetrics.totalCalls += 1;

    if (duration > 5000) {
      Sentry.addBreadcrumb({
        category: 'cloud-function',
        message: `Slow Cloud Function: ${functionName}`,
        level: 'warning',
        data: {
          functionName,
          duration,
          callId,
        },
      });
    }

    if (import.meta.env.DEV) {
      const logMessage = `✓ ${functionName}: ${Math.round(duration)}ms`;
      if (duration > 3000) {
        console.warn(logMessage);
      }
    }

    return result;
  } catch (error: any) {
    const duration = performance.now() - startTime;
    const metric = CloudFunctionsMetrics.calls[functionName];

    if (metric) {
      metric.errors += 1;
      metric.latencies.push(duration);
    }

    CloudFunctionsMetrics.totalErrors += 1;

    Sentry.captureException(error, {
      tags: {
        function: functionName,
        type: 'cloud-function-error',
      },
      contexts: {
        cloud_function_call: {
          name: functionName,
          duration,
          callId,
        },
      },
    });

    if (import.meta.env.DEV) {
      console.error(
        `%c✗ ${functionName} failed: ${error.message}`,
        'color: red'
      );
    }

    throw error;
  }
};

interface PublicMetrics {
  calls: Record<string, {
    calls: number;
    errors: number;
    errorRate: string;
    avgLatency: number;
    minLatency: number;
    maxLatency: number;
    p95Latency: number;
  }>;
  totalCalls: number;
  totalErrors: number;
  uptime: number;
  globalErrorRate?: string;
}

export const getCloudFunctionsMetrics = (): PublicMetrics => {
  const metrics: PublicMetrics = {
    calls: {},
    totalCalls: CloudFunctionsMetrics.totalCalls,
    totalErrors: CloudFunctionsMetrics.totalErrors,
    uptime: Date.now() - CloudFunctionsMetrics.startTime,
  };

  for (const [name, data] of Object.entries(CloudFunctionsMetrics.calls)) {
    metrics.calls[name] = {
      calls: data.calls,
      errors: data.errors,
      errorRate: data.calls > 0 ? ((data.errors / data.calls) * 100).toFixed(2) + '%' : '0%',
      avgLatency: Math.round(data.avgLatency),
      minLatency: data.minLatency === Infinity ? 0 : Math.round(data.minLatency),
      maxLatency: Math.round(data.maxLatency),
      p95Latency: Math.round(data.p95Latency),
    };
  }

  metrics.globalErrorRate =
    CloudFunctionsMetrics.totalCalls > 0
      ? ((CloudFunctionsMetrics.totalErrors / CloudFunctionsMetrics.totalCalls) * 100).toFixed(2) + '%'
      : '0%';

  return metrics;
};

export const resetCloudFunctionsMetrics = (): void => {
  Object.assign(CloudFunctionsMetrics, {
    calls: {},
    totalCalls: 0,
    totalErrors: 0,
    startTime: Date.now(),
  });
};

export const reportCloudFunctionsMetricsToSentry = (): PublicMetrics => {
  const metrics = getCloudFunctionsMetrics();

  Sentry.captureMessage(
    `Cloud Functions Metrics - Calls: ${metrics.totalCalls}, Errors: ${metrics.totalErrors}`,
    'info'
  );

  Sentry.addBreadcrumb({
    category: 'performance',
    message: 'Cloud Functions Metrics Report',
    level: 'info',
    data: metrics as any,
  });

  return metrics;
};

const CloudFunctionsMonitoringService = {
  trackCloudFunctionCall,
  getCloudFunctionsMetrics,
  resetCloudFunctionsMetrics,
  reportCloudFunctionsMetricsToSentry,
};

export default CloudFunctionsMonitoringService;
